import first from "lodash/first";
import { useMemo } from "react";
import { graphql, readInlineData } from "react-relay";

import { article as articleData } from "shared/data";
import { section as sectionData } from "shared/data/section";
import { notEmpty, titleCase } from "shared/lib/utils";

import { useContentHelper } from "scmp-app/components/content/hooks";
import type { hooksContentHeadlineTagContent$key } from "scmp-app/queries/__generated__/hooksContentHeadlineTagContent.graphql";

import { FlagMapping } from "./consts";

const hiddenArticleTypeIds = new Set<string>([articleData.types.explainer.entityId]);

export const isArticleTypeHidden = (entityId?: string): boolean =>
  notEmpty(entityId) && hiddenArticleTypeIds.has(entityId);

type HeadlineTagData = {
  color: string;
  entityId?: string;
  name: string;
  type: Type;
};

type SectionsV2Item = {
  value?: SectionsV2Value;
};

type SectionsV2Value = {
  name?: string;
};

type Type =
  | "column"
  | "flag"
  | "gallery"
  | "in-focus"
  | "letter"
  | "opinion"
  | "recap"
  | "section"
  | "specialCase"
  | "video";

type TypeInfo = {
  entityId: string;
  name: string;
};

export const getHeadlineTagColor = (type: Type): string => {
  const highlightTypes: Type[] = ["column", "letter", "opinion"];
  return highlightTypes.includes(type) ? "#2aa191" : "#727272";
};

type TagDetails = {
  entityId?: string;
  name: string;
};

const parseTagDetails = (
  headlineTagType: Type,
  firstType: TypeInfo | undefined,
  secondType: TypeInfo | undefined,
  contentFlag: string | undefined,
  sectionsV2: SectionsV2Item[] | undefined,
): null | TagDetails => {
  switch (headlineTagType) {
    case "column":
      return {
        entityId: secondType?.entityId ?? firstType?.entityId,
        name: secondType?.name ?? firstType?.name ?? "",
      };
    case "flag": {
      const flag = contentFlag ?? "";
      return {
        name:
          flag in FlagMapping
            ? FlagMapping[flag as keyof typeof FlagMapping]
            : (titleCase(flag) ?? ""),
      };
    }
    case "gallery":
      return {
        name: "Gallery",
      };
    case "in-focus":
      return {
        name: "In Focus",
      };
    case "letter":
      return {
        entityId: firstType?.entityId,
        name: firstType?.name ?? "Letters",
      };
    case "opinion":
      return {
        entityId: firstType?.entityId,
        name: "Opinion",
      };
    case "recap":
      return {
        name: "Recap",
      };
    case "section":
      return {
        name:
          first(
            sectionsV2
              ?.flatMap(section => section?.value)
              ?.map(section => section?.name)
              ?.filter(notEmpty)
              ?.filter(
                section =>
                  section !== sectionData.news.name && section !== sectionData.postMagazine.name,
              ) ?? [],
          ) ?? "",
      };
    case "specialCase":
      return {
        entityId: firstType?.entityId,
        name: firstType?.name ?? "",
      };
    case "video":
      return {
        name: "Video",
      };
    default:
      return null;
  }
};

export const useDetermineHeadlineTag = (
  reference: hooksContentHeadlineTagContent$key,
  useSectionTags = false,
): HeadlineTagData | null => {
  const content = readInlineData(
    graphql`
      fragment hooksContentHeadlineTagContent on Content @inline {
        __typename
        ... on Article {
          types {
            value {
              name
              entityId
            }
          }
          ...helpersCheckIsStyleArticle
        }
        flag
        sectionsV2 {
          value {
            ... on Section {
              name
            }
            ... on VideoSection {
              name
            }
          }
        }
        ...hooksContentHelperContent
      }
    `,
    reference,
  );

  const {
    isContentWithFlag,
    isEditorialOpinionType,
    isFocusArticleType,
    isLettersType,
    isRecapArticleType,
    isScmpOpinionType,
    isSpecialArticleType,
  } = useContentHelper(content);

  const headlineTagType = useMemo<null | Type>(() => {
    if (isScmpOpinionType) return "opinion";
    else if (isRecapArticleType) return "recap";
    else if (isEditorialOpinionType) return "column";
    else if (isLettersType) return "letter";
    else if (isContentWithFlag) return "flag";
    else if (isSpecialArticleType) return "specialCase";
    else if (isFocusArticleType) return "in-focus";
    else if (content.__typename === "Video") return "video";
    else if (content.__typename === "Gallery") return "gallery";
    else if (useSectionTags) return "section";
    else return null;
  }, [
    isScmpOpinionType,
    isRecapArticleType,
    isEditorialOpinionType,
    isContentWithFlag,
    isLettersType,
    isSpecialArticleType,
    isFocusArticleType,
    content.__typename,
    useSectionTags,
  ]);

  if (!notEmpty(headlineTagType)) return null;

  const [firstType, secondType] = (content.types ?? [])
    .flatMap(type => type?.value?.filter(item => notEmpty(item?.entityId)))
    .map(type => ({
      entityId: type?.entityId ?? "",
      name: type?.name ?? "",
    }));

  const tagDetails = parseTagDetails(
    headlineTagType,
    firstType,
    secondType,
    content.flag ?? undefined,
    content.sectionsV2 as SectionsV2Item[] | undefined,
  );

  if (!tagDetails || !tagDetails.name) return null;

  return {
    color: getHeadlineTagColor(headlineTagType),
    ...(tagDetails.entityId ? { entityId: tagDetails.entityId } : {}),
    name: tagDetails.name,
    type: headlineTagType,
  };
};
